import { Injectable, Signal, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  WorkflowState,
  askGenAi,
  createWorkflow,
  deleteFile,
  deleteWorkflow,
  getProcessStatus,
  getProfile,
  getSubscription,
  getWorkflow,
  initialWorkflowState,
  removeFileFromWorkflow,
  saveFileToWorkflow,
  saveProfile,
  saveWorkflow,
  setErrorState,
  updateAppProfile,
  updateWorkflow,
  uploadFile,
} from '../public-api';
import { toSignal } from '@angular/core/rxjs-interop';
import { SubscriptionService } from './api/subscription.service';
import {
  AppProfile,
  Conversation,
  FilePromptConfig,
  FileStatus,
  PromptConfig,
  Workflow,
  WorkflowPayload,
} from './types/types';

const EMPTY_WORKFLOW = {
  step: 1,
  activeStep: 1,
  creationDate: '',
  name: '',
  promptVersion: '',
  promptWorkflow: {
    userInputs: {},
    aiResults: {},
    uploadedFiles: [],
  },
  runningProcesses: [],
  resultEditing: {},
  status: '',
  subscriptionId: '',
  uid: '',
};

@Injectable({
  providedIn: 'root',
})
export class StateService {
  constructor(private store: Store<{ workflow: WorkflowState }>) {}

  subscriptionService = inject(SubscriptionService);
  stateSignal$: Signal<{
    workflow: WorkflowState;
  }> = toSignal(this.store);

  getAppState(): Signal<{ workflow: WorkflowState }> {
    return this.stateSignal$;
  }

  // Subscription

  geSubscriptionApi(): void {
    this.store.dispatch(getSubscription());
  }

  // Profile

  updateProfile(appProfile: AppProfile): void {
    this.store.dispatch(updateAppProfile({ appProfile: appProfile }));
  }

  getProfileApi(): void {
    this.store.dispatch(getProfile());
  }

  skipProfile(): void {
    this.store.dispatch(saveProfile({ profile: initialWorkflowState.profile }));
  }

  // Conversation / GenAi

  askGenAiApi(
    promptConfiguration: PromptConfig | FilePromptConfig,
    responseKey: string | string[],
    processKey: string,
    temperature: number = 0.1,
  ): void {
    const conversation: Conversation[] = [
      {
        promptConfiguration: promptConfiguration,
        addMessageHistory: false,
        parameter: {
          // maxOutputTokens: 2048,
          temperature: temperature,
          topP: 0.95,
          topK: 40,
          candidateCount: 1,
        },
      },
    ];
    this.store.dispatch(
      askGenAi({
        conversation: conversation,
        responseKey: responseKey,
        processKey: processKey,
      }),
    );
  }

  // Workflow

  createWorkflow(): void {
    this.store.dispatch(updateWorkflow({ workflow: EMPTY_WORKFLOW }));
    this.store.dispatch(createWorkflow());
  }

  updateWorkflow(workflow: Workflow): void {
    this.store.dispatch(updateWorkflow({ workflow: workflow }));
  }

  getWorkflow(workflowId: string): void {
    this.store.dispatch(getWorkflow({ workflowId: workflowId }));
  }

  saveWorkflow(worfklowPayload: WorkflowPayload, workflowId: string): void {
    this.store.dispatch(
      saveWorkflow({
        workflowPayload: worfklowPayload,
        workflowId: workflowId,
      }),
    );
  }

  deleteWorkflow(workflowId: string): void {
    this.store.dispatch(deleteWorkflow({ workflowId: workflowId }));
  }

  updateErrorState(error: boolean): void {
    this.store.dispatch(setErrorState({ error: error }));
  }

  // Upload

  uploadFile(file: File, workflow: Workflow): void {
    this.store.dispatch(uploadFile({ file, workflow }));
  }

  setFileStatus(process: FileStatus, workflow?: Workflow): void {
    this.store.dispatch(saveFileToWorkflow({ process, workflow }));
  }

  deleteFile(file: FileStatus, workflow: Workflow): void {
    this.removeFile(file, workflow);
    this.store.dispatch(deleteFile({ file, workflow }));
  }

  removeFile(file: FileStatus, workflow: Workflow): void {
    this.store.dispatch(removeFileFromWorkflow({ file, workflow }));
  }

  // Process

  getProcessStatus(processId: string): void {
    this.store.dispatch(getProcessStatus({ processId }));
  }

  // Helper

  checkUnfinishedUploads(): void {
    let uploadedFiles = [
      ...this.stateSignal$().workflow.workflow.promptWorkflow.uploadedFiles,
    ];
    uploadedFiles.forEach((uploaded) => {
      if (['queued', 'running'].includes(uploaded.status))
        this.getProcessStatus(uploaded.uid);
    });
  }

  // Postgenie

  summarizeFile(): void {
    const promptConfiguration: FilePromptConfig = {
      promptIdentifier: 'file_summary',
      replacementDict: [
        {
          replacement_key: 'fileContent',
          replacement_value:
            this.stateSignal$().workflow.workflow.promptWorkflow
              .uploadedFiles[0].result,
          replacement_type: 'file',
        },
      ],
    };
    this.askGenAiApi(promptConfiguration, 'fileSummary', 'postProcess');
  }
}
